import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { http, currency, formatApiError, API } from "../lib/api";
import { useCart } from "../lib/CartContext";
import { useAuth } from "../lib/AuthContext";
import { useSettings } from "../lib/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DraggableDesign from "../components/DraggableDesign";
import { downloadMockupPreview } from "../lib/mockupExport";
import { Upload, X, Check, ShoppingBag, Loader2, RotateCcw, Move, TrendingDown, Download } from "lucide-react";
import { toast } from "sonner";

export default function ProductPage() {
  const { productId } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");
  const [notes, setNotes] = useState("");
  const [designFile, setDesignFile] = useState(null);
  const [designPreview, setDesignPreview] = useState(null);
  const [designUrl, setDesignUrl] = useState(null);
  const [designFilename, setDesignFilename] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingPreview, setDownloadingPreview] = useState(false);
  const [designPos, setDesignPos] = useState({ top: 30, left: 30, width: 40, height: 40, rotation: 0 });
  const [lockAspect, setLockAspect] = useState(true);
  const [printingMethod, setPrintingMethod] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [withDesignService, setWithDesignService] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState([]); // ids of selected product.extra_services

  const imgSrc = (p) => (p ? (p.startsWith("http") || p.startsWith("data:") ? p : `${API}/files/${p}`) : "");
  const gallery = useMemo(() => {
    if (!product) return [];
    const imgs = product.images && product.images.length ? product.images : [];
    if (!imgs.length && product.base_image_url) return [product.base_image_url];
    return imgs;
  }, [product]);
  const mainImage = imgSrc(gallery[activeImageIdx] || product?.base_image_url || "");
  // Identify which image is the "designable" mockup — falls back to images[1] (second),
  // then images[0], then base_image_url. Admin can override via mockup_image_url.
  const mockupImageKey =
    product?.mockup_image_url ||
    product?.images?.[1] ||
    product?.images?.[0] ||
    product?.base_image_url ||
    "";
  const mockupIdx = gallery.findIndex((g) => g === mockupImageKey);
  const isMockupActive = mockupIdx === -1
    ? activeImageIdx === (gallery.length > 1 ? 1 : 0)  // legacy: prefer 2nd image if available
    : activeImageIdx === mockupIdx;
  const showDesignOverlay = isMockupActive;

  useEffect(() => {
    setLoading(true);
    http.get(`/products/${productId}`)
      .then((r) => {
        setProduct(r.data);
        if (r.data.colors?.length) setColor(r.data.colors[0]);
        if (r.data.sizes?.length) setSize(r.data.sizes[0]);
        if (r.data.printing_methods?.length) setPrintingMethod(r.data.printing_methods[0]);
        // Initialize quantity to the product's minimum
        const minQ = Math.max(1, parseInt(r.data.min_qty || 1, 10));
        setQuantity(minQ);
        // Initialize design position from product's mockup default
        setDesignPos({
          top: r.data.mockup?.top ?? 30,
          left: r.data.mockup?.left ?? 30,
          width: r.data.mockup?.width ?? 40,
          height: r.data.mockup?.height ?? 40,
          rotation: 0,
        });
        // Focus the mockup (designable) image by default — prefer the 2nd image so
        // the first one keeps its role as "product hero" in the gallery.
        const imgs = (r.data.images && r.data.images.length) ? r.data.images : [r.data.base_image_url].filter(Boolean);
        const mk = r.data.mockup_image_url || imgs[1] || imgs[0];
        const idx = imgs.findIndex((g) => g === mk);
        setActiveImageIdx(idx >= 0 ? idx : (imgs.length > 1 ? 1 : 0));
      })
      .catch(() => nav("/tienda"))
      .finally(() => setLoading(false));
  }, [productId, nav]);

  // Effective unit price considering variants and applicable bulk rule
  // Note: Design service + extra services are FLAT one-time fees (added once to line total, not per unit)
  const { unitPrice, appliedRule, linePrice, savings, designFeeApplied, extrasFeeApplied, extrasSnapshot } = useMemo(() => {
    if (!product) return { unitPrice: 0, appliedRule: null, linePrice: 0, savings: 0, designFeeApplied: 0, extrasFeeApplied: 0, extrasSnapshot: [] };
    let baseUnit = Number(product.base_price) + Number(color?.extra_price || 0) + Number(size?.extra_price || 0);
    baseUnit += Number(printingMethod?.extra_price || 0);
    const rules = product.bulk_rules || [];
    const applicable = rules.filter((r) => quantity >= Number(r.min_qty));
    const best = applicable.length
      ? applicable.reduce((a, b) => (Number(a.price_off) > Number(b.price_off) ? a : b))
      : null;
    const off = best ? Number(best.price_off) : 0;
    const unit = Math.max(0, baseUnit - off);
    const designFee = withDesignService && product.design_service?.enabled
      ? Number(product.design_service?.price || 0)
      : 0;
    const enabledExtras = (product.extra_services || []).filter((s) => s.enabled !== false);
    const snap = enabledExtras
      .filter((s) => selectedExtras.includes(s.id))
      .map((s) => ({ id: s.id, name: s.name, price: Number(s.price || 0) }));
    const extrasFee = snap.reduce((acc, s) => acc + s.price, 0);
    return {
      unitPrice: unit,
      appliedRule: best,
      linePrice: unit * quantity + designFee + extrasFee,
      savings: off * quantity,
      designFeeApplied: designFee,
      extrasFeeApplied: extrasFee,
      extrasSnapshot: snap,
    };
  }, [product, color, size, quantity, printingMethod, withDesignService, selectedExtras]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)) {
      toast.error("Solo imágenes PNG, JPG, WEBP o GIF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Máximo 10 MB");
      return;
    }
    setDesignFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setDesignPreview(dataUrl);
      // Read natural size and fit the design box preserving the real aspect ratio.
      // The mockup container is square (1:1) so width% and height% map to identical
      // pixel dimensions — we scale one axis to keep the design uncropped.
      const probe = new Image();
      probe.onload = () => {
        const nw = probe.naturalWidth || 1;
        const nh = probe.naturalHeight || 1;
        const ratio = nw / nh;
        // Start from the product-configured mockup box (max square area)
        const maxSide = Math.max(
          Number(product?.mockup?.width ?? 40),
          Number(product?.mockup?.height ?? 40)
        );
        let w, h;
        if (ratio >= 1) {
          // wider than tall → cap by width
          w = maxSide;
          h = Math.max(5, Math.min(90, maxSide / ratio));
        } else {
          // taller than wide → cap by height
          h = maxSide;
          w = Math.max(5, Math.min(90, maxSide * ratio));
        }
        // Center in the mockup area
        setDesignPos((prev) => ({
          ...prev,
          top: Math.max(0, 50 - h / 2),
          left: Math.max(0, 50 - w / 2),
          width: w,
          height: h,
          rotation: 0,
        }));
        setLockAspect(true); // lock aspect by default so drag corners keep proportions
      };
      probe.src = dataUrl;
    };
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await http.post("/uploads/design", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDesignUrl(data.storage_path);
      setDesignFilename(data.filename);
      toast.success("Diseño subido — arrastra y redimensiona en el mockup");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDesignFile(null);
      setDesignPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removeDesign = () => {
    setDesignFile(null);
    setDesignPreview(null);
    setDesignUrl(null);
    setDesignFilename(null);
  };

  const resetPosition = () => {
    setDesignPos({
      top: product.mockup?.top ?? 30,
      left: product.mockup?.left ?? 30,
      width: product.mockup?.width ?? 40,
      height: product.mockup?.height ?? 40,
      rotation: 0,
    });
  };

  const rotateBy = (delta) => {
    setDesignPos((d) => ({ ...d, rotation: (Number(d.rotation || 0) + delta) % 360 }));
  };

  const addToCart = () => {
    if (!product) return;
    addItem({
      product_id: product.product_id,
      product_name: product.name,
      product_image: product.base_image_url,
      quantity,
      color_value: color?.value,
      color_label: color?.label,
      color_hex: color?.hex,
      size_value: size?.value,
      size_label: size?.label,
      custom_width: customWidth ? Number(customWidth) : null,
      custom_height: customHeight ? Number(customHeight) : null,
      design_url: designUrl,
      design_filename: designFilename,
      design_position: designPos,
      printing_method: printingMethod?.code,
      printing_method_label: printingMethod?.label,
      with_design_service: withDesignService,
      design_service_price: withDesignService && product.design_service?.enabled ? Number(product.design_service.price || 0) : 0,
      selected_extra_service_ids: extrasSnapshot.map((s) => s.id),
      selected_extra_services: extrasSnapshot, // denormalized for cart UI
      notes,
      unit_price: unitPrice,
      line_total: linePrice,
      mockup: product.mockup,
    });
    toast.success("Agregado al carrito");
    nav("/carrito");
  };

  if (loading || !product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-neutral-400" />
      </div>
    );
  }

  const designSrc = designPreview || (designUrl ? `${API}/files/${designUrl}` : null);

  const handleDownloadPreview = async () => {
    if (!designSrc) {
      toast.error("Sube un diseño primero para generar la vista previa");
      return;
    }
    setDownloadingPreview(true);
    try {
      const brand = settings?.brand_name || "Printlab";
      const logo = settings?.logo_url || null;
      const slug = (product?.name || "producto")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      await downloadMockupPreview({
        mockupImageUrl: mainImage,
        designSrc,
        designPos,
        colorHex: color?.hex,
        blendMode: product?.mockup?.blend_mode || "multiply",
        logoUrl: logo,
        watermarkText: brand.toUpperCase(),
        size: 1400,
        filename: `mockup-${slug || "producto"}.png`,
      });
      toast.success("Vista previa descargada");
    } catch (e) {
      toast.error("No se pudo generar la vista previa. Intenta de nuevo.");
    } finally {
      setDownloadingPreview(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="product-page">
      <Link to="/tienda" className="text-xs font-mono uppercase text-neutral-500 hover:text-black">← Volver</Link>

      <div className="grid lg:grid-cols-12 gap-8 mt-6">
          {/* Sticky mockup */}
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-24">
              <div className="brutal-card p-6 md:p-10 bg-neutral-50">
                <div className="relative aspect-square bg-neutral-100 overflow-hidden" data-testid="mockup-container">
                  {color?.hex && <div className="absolute inset-0" style={{ backgroundColor: color.hex, opacity: 0.9 }} />}
                  <img
                    src={mainImage}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    draggable={false}
                    style={{ mixBlendMode: color?.hex && showDesignOverlay ? "multiply" : "normal" }}
                  />
                  {designSrc && showDesignOverlay && (
                    <DraggableDesign
                      src={designSrc}
                      value={designPos}
                      onChange={setDesignPos}
                      editable={true}
                      blendMode={product.mockup?.blend_mode || "multiply"}
                      lockAspect={lockAspect}
                    />
                  )}
                  {/* Badge indicating whether this view is editable */}
                  {showDesignOverlay ? (
                    <div className="absolute top-3 left-3 bg-purple-600 text-white text-[10px] font-mono uppercase px-2 py-1" data-testid="mockup-editable-badge">
                      ✎ Diseña aquí
                    </div>
                  ) : (
                    <div className="absolute top-3 left-3 bg-neutral-800/85 text-white text-[10px] font-mono uppercase px-2 py-1" data-testid="mockup-view-only-badge">
                      Vista solo referencia
                    </div>
                  )}
                  {!showDesignOverlay && mockupIdx >= 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveImageIdx(mockupIdx)}
                      className="absolute bottom-3 right-3 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium px-3 py-2 shadow-lg"
                      data-testid="mockup-back-to-editable"
                    >
                      Volver al mockup editable →
                    </button>
                  )}
                  {/* Download preview (only when the mockup is active AND a design has been uploaded) */}
                  {showDesignOverlay && designSrc && (
                    <button
                      type="button"
                      onClick={handleDownloadPreview}
                      disabled={downloadingPreview}
                      className="absolute bottom-3 right-3 bg-black hover:bg-[#FF3B30] text-white text-xs font-medium px-3 py-2 shadow-lg flex items-center gap-1.5 disabled:opacity-60"
                      data-testid="mockup-download-preview"
                      title="Descarga una imagen PNG del mockup con marca de agua"
                    >
                      {downloadingPreview ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      Descargar vista previa
                    </button>
                  )}
                </div>
                {gallery.length > 1 && (
                  <div className="mt-3 grid grid-cols-5 gap-2" data-testid="product-gallery">
                    {gallery.map((g, i) => {
                      const isMockThumb = g === mockupImageKey || (mockupIdx === -1 && i === 0);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setActiveImageIdx(i)}
                          className={`relative aspect-square border-2 overflow-hidden ${
                            activeImageIdx === i
                              ? (isMockThumb ? "border-purple-500" : "border-black")
                              : (isMockThumb ? "border-purple-300 hover:border-purple-500" : "border-neutral-200 hover:border-neutral-400")
                          }`}
                          data-testid={`gallery-thumb-${i}`}
                          title={isMockThumb ? "Imagen sobre la que se aplica tu diseño" : "Imagen de referencia"}
                        >
                          <img src={imgSrc(g)} alt={`v${i}`} className="w-full h-full object-cover" />
                          {isMockThumb && (
                            <div className="absolute top-0 left-0 bg-purple-600 text-white text-[8px] font-mono uppercase px-1" data-testid={`gallery-thumb-mockup-badge-${i}`}>
                              Diseño
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                {designFile ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-mono text-neutral-600 flex items-center gap-1">
                      <Move className="w-3 h-3" /> Arrastra · redimensiona · rota
                    </span>
                    <label className="flex items-center gap-1.5 text-xs font-mono text-neutral-600 cursor-pointer" data-testid="lock-aspect-toggle-label">
                      <input
                        type="checkbox"
                        checked={lockAspect}
                        onChange={(e) => setLockAspect(e.target.checked)}
                        data-testid="lock-aspect-toggle"
                      />
                      Proporcional
                    </label>
                    <button onClick={() => rotateBy(-15)} className="text-xs font-mono text-neutral-500 hover:text-black px-1.5 py-0.5 border border-neutral-200" data-testid="rotate-left-btn">
                      −15°
                    </button>
                    <button onClick={() => rotateBy(15)} className="text-xs font-mono text-neutral-500 hover:text-black px-1.5 py-0.5 border border-neutral-200" data-testid="rotate-right-btn">
                      +15°
                    </button>
                    <button onClick={resetPosition} className="text-xs font-mono text-neutral-500 hover:text-black flex items-center gap-1" data-testid="reset-position-btn">
                      <RotateCcw className="w-3 h-3" /> Restablecer
                    </button>
                  </div>
                ) : (
                  <span className="text-xs font-mono uppercase text-neutral-500">Sube un diseño para editarlo aquí</span>
                )}
                {uploading && (
                  <span className="text-xs font-mono text-neutral-500 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Subiendo...
                  </span>
                )}
              </div>
              {designFile && (
                <div className="mt-3 grid grid-cols-5 gap-2 text-xs font-mono text-neutral-500 border-t border-neutral-200 pt-3">
                  <div>X: {designPos.left.toFixed(0)}%</div>
                  <div>Y: {designPos.top.toFixed(0)}%</div>
                  <div>W: {designPos.width.toFixed(0)}%</div>
                  <div>H: {designPos.height.toFixed(0)}%</div>
                  <div data-testid="rotation-readout">↻ {Number(designPos.rotation || 0).toFixed(0)}°</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Config */}
        <div className="lg:col-span-5 space-y-6">
          <div>
            <div className="text-xs font-mono uppercase text-neutral-500">{product.category}</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-1">{product.name}</h1>
            <div className="mt-3 flex items-baseline gap-3">
              <div className="font-display text-3xl">{currency(linePrice)}</div>
              {savings > 0 && (
                <div className="text-xs font-mono text-emerald-600" data-testid="product-savings">
                  Ahorras {currency(savings)}
                </div>
              )}
            </div>
            <div className="text-xs font-mono text-neutral-500 mt-1">
              {currency(unitPrice)} / unidad
              {appliedRule && (
                <span className="ml-2 text-[#FF3B30]">· Precio de mayoreo aplicado</span>
              )}
            </div>

            {/* Price breakdown — visible when a design service or extra services are selected */}
            {(designFeeApplied > 0 || extrasFeeApplied > 0) && (
              <div className="mt-4 border border-neutral-200 bg-neutral-50 p-3 text-sm" data-testid="product-price-breakdown">
                <div className="text-[10px] font-mono uppercase text-neutral-500 mb-2">Desglose del precio</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>{quantity} × {currency(unitPrice)}</span>
                    <span className="font-mono">{currency(unitPrice * quantity)}</span>
                  </div>
                  {designFeeApplied > 0 && (
                    <div className="flex justify-between text-purple-700" data-testid="breakdown-design-service">
                      <span>+ Servicio de diseño gráfico <span className="text-[10px] font-mono uppercase text-purple-500">único</span></span>
                      <span className="font-mono">{currency(designFeeApplied)}</span>
                    </div>
                  )}
                  {extrasSnapshot.map((svc) => (
                    <div key={svc.id} className="flex justify-between text-emerald-700" data-testid={`breakdown-extra-${svc.id}`}>
                      <span>+ {svc.name} <span className="text-[10px] font-mono uppercase text-emerald-500">único</span></span>
                      <span className="font-mono">{currency(svc.price)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-neutral-300 pt-1 mt-1 font-medium">
                    <span>Total línea</span>
                    <span className="font-mono">{currency(linePrice)}</span>
                  </div>
                </div>
              </div>
            )}
            <p className="text-sm text-neutral-600 mt-3">{product.description}</p>
            <p className="text-xs text-neutral-500 mt-2 font-mono">Entrega estimada: {product.delivery_days} días hábiles</p>
          </div>

          {/* Bulk pricing ladder */}
          {product.bulk_rules && product.bulk_rules.length > 0 && (
            <div className="border border-[#FF3B30]/40 bg-red-50/30 p-4" data-testid="bulk-rules">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-4 h-4 text-[#FF3B30]" />
                <div className="font-medium text-sm">Precios por mayoreo</div>
              </div>
              <div className="space-y-1">
                {[...product.bulk_rules].sort((a, b) => a.min_qty - b.min_qty).map((r, i) => {
                  const active = appliedRule && appliedRule.min_qty === r.min_qty;
                  return (
                    <div
                      key={i}
                      className={`flex items-center justify-between text-xs px-2 py-1.5 ${active ? "bg-[#FF3B30] text-white" : "text-neutral-700"}`}
                      data-testid={`bulk-rule-${i}`}
                    >
                      <span>Desde {r.min_qty} pzs</span>
                      <span className="font-mono">−{currency(r.price_off)}/unidad</span>
                      <span className="font-medium">{currency(Math.max(0, Number(product.base_price) + Number(color?.extra_price || 0) + Number(size?.extra_price || 0) - Number(r.price_off)))} c/u</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Colors */}
          {product.colors?.length > 0 && (
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Color</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {product.colors.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setColor(c)}
                    className={`px-3 py-2 border text-sm flex items-center gap-2 transition-colors ${
                      color?.value === c.value ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"
                    }`}
                    data-testid={`product-color-${c.value}`}
                  >
                    {c.hex && <span className="w-4 h-4 border border-neutral-300" style={{ background: c.hex }} />}
                    {c.label}
                    {c.extra_price > 0 && <span className="text-[10px] opacity-70">+{currency(c.extra_price)}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          {product.sizes?.length > 0 && (
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Talla / Tamaño</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {product.sizes.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setSize(s)}
                    className={`px-4 py-2 border text-sm min-w-[56px] transition-colors ${
                      size?.value === s.value ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"
                    }`}
                    data-testid={`product-size-${s.value}`}
                  >
                    {s.label}
                    {s.extra_price > 0 && <span className="text-[10px] opacity-70 ml-1">+{currency(s.extra_price)}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.has_custom_measure && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cw" className="text-xs font-mono uppercase text-neutral-500">Ancho (cm)</Label>
                <Input id="cw" type="number" value={customWidth} onChange={(e) => setCustomWidth(e.target.value)}
                       className="rounded-none" data-testid="product-custom-width" />
              </div>
              <div>
                <Label htmlFor="ch" className="text-xs font-mono uppercase text-neutral-500">Alto (cm)</Label>
                <Input id="ch" type="number" value={customHeight} onChange={(e) => setCustomHeight(e.target.value)}
                       className="rounded-none" data-testid="product-custom-height" />
              </div>
            </div>
          )}

          {/* Printing method */}
          {product.printing_methods?.length > 0 && (
            <div data-testid="printing-methods">
              <Label className="text-xs font-mono uppercase text-neutral-500">Método de impresión</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {product.printing_methods.map((m) => {
                  const finalUnit = Math.max(0, Number(product.base_price) + Number(color?.extra_price || 0) + Number(size?.extra_price || 0) + Number(m.extra_price || 0));
                  const isActive = printingMethod?.code === m.code;
                  return (
                    <button
                      key={m.code}
                      onClick={() => setPrintingMethod(m)}
                      className={`p-3 border text-left transition-colors ${
                        isActive ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"
                      }`}
                      data-testid={`printing-method-${m.code}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">{m.label}</span>
                        <span className="font-display text-base" data-testid={`printing-method-price-${m.code}`}>{currency(finalUnit)}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        {m.description ? (
                          <div className="text-[11px] opacity-70 leading-snug">{m.description}</div>
                        ) : <span />}
                        {Number(m.extra_price) !== 0 && (
                          <span className="text-[10px] opacity-70 font-mono ml-2 flex-shrink-0">
                            {Number(m.extra_price) > 0 ? "+" : "−"}{currency(Math.abs(Number(m.extra_price)))}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Design service */}
          {product.design_service?.enabled && (
            <label
              className={`block border-2 p-4 cursor-pointer transition-colors ${
                withDesignService ? "border-purple-500 bg-purple-50/50" : "border-neutral-200 hover:border-purple-400"
              }`}
              data-testid="design-service-toggle"
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={withDesignService}
                  onChange={(e) => setWithDesignService(e.target.checked)}
                  data-testid="design-service-checkbox"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm">
                      Servicio de diseño gráfico
                    </div>
                    <div className="text-right">
                      <div className="font-display text-lg" data-testid="design-service-price">
                        +{currency(product.design_service.price)}
                      </div>
                      <div className="text-[10px] font-mono uppercase text-purple-700">cargo único</div>
                    </div>
                  </div>
                  <div className="text-xs text-neutral-600 mt-1">{product.design_service.description}</div>
                  <div className="text-[11px] text-neutral-500 mt-1 italic">Se cobra una sola vez por producto, no por unidad. Nuestro equipo crea tu diseño — no necesitas subir imagen.</div>
                </div>
              </div>
            </label>
          )}

          {/* Extra services (customizable, per product) */}
          {product.extra_services && product.extra_services.filter((s) => s.enabled !== false).length > 0 && (
            <div className="space-y-2" data-testid="extra-services-block">
              <Label className="text-xs font-mono uppercase text-neutral-500">Servicios adicionales</Label>
              {product.extra_services.filter((s) => s.enabled !== false).map((svc) => {
                const on = selectedExtras.includes(svc.id);
                return (
                  <label
                    key={svc.id}
                    className={`block border-2 p-3 cursor-pointer transition-colors ${
                      on ? "border-emerald-500 bg-emerald-50/60" : "border-neutral-200 hover:border-emerald-400"
                    }`}
                    data-testid={`extra-service-${svc.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 accent-emerald-600"
                        checked={on}
                        onChange={(e) => {
                          setSelectedExtras((prev) =>
                            e.target.checked
                              ? [...prev.filter((x) => x !== svc.id), svc.id]
                              : prev.filter((x) => x !== svc.id)
                          );
                        }}
                        data-testid={`extra-service-checkbox-${svc.id}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-sm">{svc.name}</div>
                          <div className="text-right">
                            <div className="font-display text-base" data-testid={`extra-service-price-${svc.id}`}>
                              +{currency(svc.price)}
                            </div>
                            <div className="text-[10px] font-mono uppercase text-emerald-700">cargo único</div>
                          </div>
                        </div>
                        {svc.description && (
                          <div className="text-xs text-neutral-600 mt-1">{svc.description}</div>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {/* Quantity */}
          <div>
            <Label className="text-xs font-mono uppercase text-neutral-500">Cantidad</Label>
            <div className="flex items-center border border-neutral-200 w-40 mt-2">
              <button
                onClick={() => setQuantity((q) => Math.max(Math.max(1, parseInt(product.min_qty || 1, 10)), q - 1))}
                className="px-3 py-2 hover:bg-neutral-100"
                data-testid="product-qty-minus"
              >−</button>
              <input
                type="number"
                value={quantity}
                min={Math.max(1, parseInt(product.min_qty || 1, 10))}
                onChange={(e) => {
                  const mq = Math.max(1, parseInt(product.min_qty || 1, 10));
                  setQuantity(Math.max(mq, Number(e.target.value) || mq));
                }}
                className="w-full text-center py-2 border-x border-neutral-200 outline-none"
                data-testid="product-qty-input"
              />
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="px-3 py-2 hover:bg-neutral-100"
                data-testid="product-qty-plus"
              >+</button>
            </div>
            {product.min_qty && product.min_qty > 1 && (
              <div className="text-xs text-[#FF3B30] mt-2 font-medium" data-testid="product-min-qty-notice">
                Pedido mínimo: {product.min_qty} piezas
              </div>
            )}
            {product.bulk_rules?.length > 0 && !appliedRule && (
              <div className="text-xs text-neutral-500 mt-2">
                Compra <span className="font-mono font-medium">{[...product.bulk_rules].sort((a,b)=>a.min_qty-b.min_qty)[0].min_qty}</span> o más y ahorra en cada pieza.
              </div>
            )}
          </div>

          {/* Design upload */}
          <div>
            <Label className="text-xs font-mono uppercase text-neutral-500">Tu diseño (opcional)</Label>
            {designFile ? (
              <div className="mt-2 border border-neutral-200 p-3 flex items-center gap-3">
                <div className="w-12 h-12 bg-neutral-100 border border-neutral-200 flex-shrink-0 overflow-hidden">
                  {designPreview && <img src={designPreview} alt="preview" className="w-full h-full object-contain" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{designFile.name}</div>
                  <div className="text-xs text-neutral-500 font-mono">
                    {(designFile.size / 1024).toFixed(1)} KB · <Check className="w-3 h-3 inline text-emerald-600" /> subido
                  </div>
                </div>
                <button onClick={removeDesign} className="p-2 hover:bg-neutral-100" data-testid="product-design-remove">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="design-file"
                className="mt-2 border border-dashed border-neutral-300 p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-black transition-colors bg-neutral-50"
                data-testid="product-design-dropzone"
              >
                <Upload className="w-5 h-5 text-neutral-400" />
                <span className="text-sm">Arrastra tu imagen o haz click</span>
                <span className="text-xs text-neutral-500 font-mono">PNG, JPG, WEBP · Max 10 MB</span>
                <input
                  id="design-file"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="product-design-input"
                />
              </label>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="text-xs font-mono uppercase text-neutral-500">Notas / Instrucciones</Label>
            <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={3} className="rounded-none mt-2" placeholder="Ej. centrar el diseño, colores exactos, etc."
                      data-testid="product-notes" />
          </div>

          <Button
            onClick={addToCart}
            size="lg"
            disabled={uploading}
            className="w-full h-14 brutal-btn bg-[#FF3B30] hover:bg-black text-white text-base"
            data-testid="product-add-to-cart"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            Agregar al carrito · {currency(linePrice)}
          </Button>
        </div>
      </div>
    </div>
  );
}
